import { useMemo, type ReactNode } from 'react';
import Markdown from 'markdown-to-jsx';
import {
  Typography,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Box,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';

import type { MarkdownContentProps } from './markdown-content.types';

import { StreamingCursor } from '../streaming-indicator.component';

const MONOSPACE_FONT_FAMILY = '"Roboto Mono", "Consolas", "Monaco", monospace';

const createMarkdownOptions = (theme: Theme) => ({
  wrapper: ({ children }: { children: ReactNode }) => <>{children}</>,
  overrides: {
    h1: { component: Typography, props: { variant: 'h4', sx: { mt: 3, mb: 1.5 } } },
    h2: { component: Typography, props: { variant: 'h5', sx: { mt: 3, mb: 1.5 } } },
    h3: { component: Typography, props: { variant: 'h6', sx: { mt: 3, mb: 1 } } },
    h4: { component: Typography, props: { variant: 'subtitle1', sx: { mt: 3, mb: 1 } } },
    p: {
      component: Typography,
      props: {
        variant: 'body1',
        paragraph: true,
        sx: { fontSize: 'inherit', lineHeight: 'inherit' },
      },
    },
    a: { component: Link, props: { target: '_blank', rel: 'noopener noreferrer' } },
    table: {
      component: ({ children }: { children: ReactNode }) => (
        <TableContainer
          sx={{
            my: 2,
            bgcolor: 'white',
            border: `1px solid ${theme.palette.drive.border}`,
            borderRadius: '8px',
          }}
        >
          <Table size='small'>{children}</Table>
        </TableContainer>
      ),
    },
    thead: { component: TableHead },
    tbody: { component: TableBody },
    tr: { component: TableRow },
    th: {
      component: TableCell,
      props: { sx: { fontWeight: theme.typography.fontWeightBold } },
    },
    td: { component: TableCell },
    pre: {
      component: ({ children }: { children: ReactNode }) => (
        <Box
          component='pre'
          sx={{
            p: 2,
            overflow: 'auto',
            bgcolor: 'grey.50',
            border: `1px solid ${theme.palette.drive.border}`,
            borderRadius: '8px',
            fontFamily: MONOSPACE_FONT_FAMILY,
            fontSize: theme.typography.body2.fontSize,
          }}
        >
          {children}
        </Box>
      ),
    },
    code: {
      component: ({ children, className }: { children: ReactNode; className?: string }) => {
        const isInline = !className;
        return isInline ? (
          <Box
            component='code'
            sx={{
              bgcolor: 'grey.100',
              px: 0.5,
              py: 0.25,
              borderRadius: theme.shape.borderRadius / 2,
              fontFamily: MONOSPACE_FONT_FAMILY,
              fontSize: theme.typography.body2.fontSize,
            }}
          >
            {children}
          </Box>
        ) : (
          <code>{children}</code>
        );
      },
    },
    blockquote: {
      component: ({ children }: { children: ReactNode }) => (
        <Box
          component='blockquote'
          sx={{
            borderLeft: `${theme.spacing(0.5)} solid`,
            borderColor: 'primary.main',
            pl: 2,
            my: 2,
            py: 1,
            bgcolor: 'grey.50',
            fontStyle: 'italic',
          }}
        >
          {children}
        </Box>
      ),
    },
    hr: {
      component: () => (
        <Box component='hr' sx={{ my: 3, border: 'none', borderTop: 1, borderColor: 'divider' }} />
      ),
    },
    ul: {
      component: Box,
      props: { component: 'ul', sx: { pl: 3, mt: 1.5, mb: 3, '& ul, & ol': { my: 1 } } },
    },
    ol: {
      component: Box,
      props: { component: 'ol', sx: { pl: 3, mt: 1.5, mb: 3, '& ul, & ol': { my: 1 } } },
    },
    li: {
      component: Typography,
      props: {
        component: 'li',
        variant: 'body1',
        sx: { mb: 0.5, fontSize: 'inherit', lineHeight: 'inherit' },
      },
    },
    strong: { component: 'strong' as const },
    em: { component: 'em' as const },
    cursor: { component: StreamingCursor },
  },
});

export const MarkdownContent = ({ content, showCursor }: MarkdownContentProps) => {
  const theme = useTheme();
  const markdownOptions = useMemo(() => createMarkdownOptions(theme), [theme]);
  const processedContent = showCursor ? `${content}<cursor/>` : content;

  return (
    <Box
      sx={{
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
      }}
    >
      <Markdown options={markdownOptions}>{processedContent}</Markdown>
    </Box>
  );
};
